/**
 * AICoachContinueButton Component
 *
 * Continue button with states:
 * - Actionable: Show "Continue to Next Step" (sun/yellow) - starts directly
 * - When AI speaking: Show "AI Speaking..." (green, pulsing, disabled)
 * - When session complete: Show leave button (green)
 *
 * Responsive:
 * - Mobile: Smaller button with less padding
 * - Desktop: Full-size button
 */

import React from 'react'
import { Box, Button, CircularProgress, Tooltip, keyframes } from '@mui/material'
import { CheckCircleIcon, ArrowRightIcon, SparklesIcon } from '@heroicons/react/24/solid'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { TESTIDS } from '../../../../../config/testids'
import { STEP_TITLE_KEYS, AI_COACH_STYLES } from '../constants'
import { AI_COACH_LAST_PART, type AICoachPart } from '../../../../../../../pure-js/types/AICoach'

// Speaking animation for AI
const speakingPulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 4px 16px rgba(74, 222, 128, 0.4); }
  50% { transform: scale(1.02); box-shadow: 0 4px 24px rgba(74, 222, 128, 0.6); }
`

interface AICoachContinueButtonProps {
  currentPart: AICoachPart | null
  isAISpeaking: boolean
  isButtonLoading: boolean
  isContentReady: boolean
  /** The coach really is still generating this part. "Not ready" alone is not a reason to say so. */
  isPreparingContent: boolean
  onContinue: () => void
  onLeave: () => void
}

export const AICoachContinueButton: React.FC<AICoachContinueButtonProps> = ({
  currentPart,
  isAISpeaking,
  isButtonLoading,
  isContentReady,
  isPreparingContent,
  onContinue,
  onLeave
}) => {
  const { t } = useTranslation()

  // Session is completed when currentPart is null (intro + all 3 parts done)
  const isSessionComplete = currentPart === null

  // Button is disabled when AI is speaking or content is not ready (NOT when session is complete - that should be clickable)
  const isButtonDisabled = isAISpeaking || (!isSessionComplete && !isContentReady)

  // Build tooltip text
  const getTooltipText = (): string => {
    if (isPreparingContent) {
      return t('aiCoach.tooltips.preparingContentHint')
    }
    if (isAISpeaking) {
      return t('aiCoach.tooltips.aiSpeaking')
    }
    if (isSessionComplete) {
      return t('aiCoach.tooltips.sessionCompletedLeaveCall')
    }
    const nextStepKey = currentPart < AI_COACH_LAST_PART ? STEP_TITLE_KEYS[(currentPart + 1) as AICoachPart] : null
    return t('aiCoach.tooltips.proceedTo', { step: nextStepKey ? t(nextStepKey) : t('aiCoach.ui.completeSession') })
  }

  // Get button text
  const getButtonText = (): string => {
    if (isButtonLoading) {
      return ''
    }
    if (isPreparingContent) {
      return t('aiCoach.buttons.preparingContentLabel')
    }
    if (isAISpeaking) {
      return t('aiCoach.buttons.aiSpeaking')
    }
    if (isSessionComplete) {
      return t('aiCoach.buttons.sessionCompleteLeave')
    }
    return t('aiCoach.buttons.continueToNextStep')
  }

  // Get button icon
  const getButtonIcon = () => {
    if (isAISpeaking) {
      return <SparklesIcon style={{ width: 18, height: 18 }} />
    }
    if (isButtonLoading || isPreparingContent) {
      return null
    }
    if (isSessionComplete) {
      return <CheckCircleIcon style={{ width: 18, height: 18 }} />
    }
    return <ArrowRightIcon style={{ width: 18, height: 18 }} />
  }

  // Determine button background color
  const getButtonBackgroundColor = (): string => {
    if (isPreparingContent) return INTERAX_COLORS.thunderGrey
    if (isAISpeaking) return INTERAX_COLORS.green
    if (isSessionComplete) return INTERAX_COLORS.green
    return INTERAX_COLORS.sun
  }

  // Determine button hover color
  const getButtonHoverColor = (): string => {
    if (isPreparingContent) return INTERAX_COLORS.thunderGrey
    if (isAISpeaking) return INTERAX_COLORS.green
    if (isSessionComplete) return '#16a34a' // Darker green on hover
    return '#e6a800'
  }

  // Determine button shadow
  const getButtonBoxShadow = (): string => {
    if (isAISpeaking) return '0 4px 16px rgba(74, 222, 128, 0.4)'
    if (isPreparingContent) return 'none'
    return '0 4px 16px rgba(255, 171, 64, 0.4)'
  }

  // Handle button click - navigate to dashboard when session is complete
  const handleButtonClick = () => {
    if (isSessionComplete) {
      onLeave()
    } else {
      onContinue()
    }
  }

  return (
    <Tooltip title={getTooltipText()}>
      <span>
        <Button
          variant="contained"
          endIcon={getButtonIcon()}
          onClick={handleButtonClick}
          disabled={isButtonDisabled || isButtonLoading}
          data-testid={TESTIDS.AI_COACH_CONTINUE_BTN}
          sx={{
            backgroundColor: getButtonBackgroundColor(),
            color: '#000',
            fontWeight: 600,
            fontSize: { xs: 12, sm: 13, md: 14, lg: 14 },
            padding: { xs: '10px 16px', sm: '11px 16px', md: '12px 18px', lg: '12px 18px' },
            borderRadius: AI_COACH_STYLES.borderRadius.medium,
            whiteSpace: 'nowrap',
            boxShadow: getButtonBoxShadow(),
            minWidth: { xs: 'auto', sm: '200px' },
            ...(isAISpeaking && { animation: `${speakingPulse} 1.5s ease-in-out infinite` }),
            '&:hover': { backgroundColor: getButtonHoverColor() },
            '&:disabled': {
              backgroundColor: isAISpeaking ? INTERAX_COLORS.green : INTERAX_COLORS.thunderGrey,
              color: isAISpeaking ? '#000' : INTERAX_COLORS.white40,
              boxShadow: isAISpeaking ? '0 4px 16px rgba(74, 222, 128, 0.4)' : 'none',
              ...(isAISpeaking && { animation: `${speakingPulse} 1.5s ease-in-out infinite` })
            }
          }}
        >
          {isButtonLoading ? (
            <CircularProgress size={20} sx={{ color: '#000' }} />
          ) : isPreparingContent ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} sx={{ color: INTERAX_COLORS.white40 }} />
              {getButtonText()}
            </Box>
          ) : (
            getButtonText()
          )}
        </Button>
      </span>
    </Tooltip>
  )
}
